import { useRouteMatch } from 'react-router-dom';

import { DEMO_CONNECTION_INDEX } from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useSavedConnectionsQuery } from 'src/ui/graph/types';
import { useGetLastConnectionIndex } from 'src/ui/hooks/connection';

export const useOrgDomain = () => {
  const routeMatch = useRouteMatch<{ org: string }>({
    path: ['/o/:org([a-z0-9-]+)'],
  });

  return routeMatch?.params.org || '';
};

export const useCurrentConnectionIdx = (): string => {
  const routeMatch = useRouteMatch<{ org?: string; id?: string }>({
    path: ['/u/:id(\\d+|demo)', '/o/:org([a-z0-9-]+)/:id(\\d+)'],
  });

  // integer connection idx is valid
  if (routeMatch?.params.id && !Number.isNaN(parseInt(routeMatch.params.id))) {
    return routeMatch.params.id;
  }

  // demo connection is valid
  if (routeMatch?.params.id === 'demo') {
    return DEMO_CONNECTION_INDEX.toString();
  }

  // invalid connection idx
  return '';
};

export const useLandingConnectionIdx = (): string => {
  const org = useOrgDomain();
  const connectionIdx = useCurrentConnectionIdx();
  const lastConnectionIndex = useGetLastConnectionIndex(org);
  const { data } = useSavedConnectionsQuery(
    graphQLClient,
    org ? { org_domain: org } : {},
    {
      enabled:
        !connectionIdx && !org && lastConnectionIndex !== DEMO_CONNECTION_INDEX,
    }
  );

  if (connectionIdx) {
    return connectionIdx;
  }

  if (
    !org &&
    lastConnectionIndex !== null &&
    (lastConnectionIndex === DEMO_CONNECTION_INDEX ||
      data?.listConnections?.some((c) => c?.index === lastConnectionIndex))
  ) {
    // use the last connection index only when it exists
    // not applicable for orgs (no root landing page without connection id)
    return lastConnectionIndex.toString();
  }

  if (!org && data?.listConnections?.length) {
    // fall back to the first available connection when nothing else is selected
    // not applicable for orgs (no root landing page without connection id)
    const firstIndex = data.listConnections[0]?.index;
    if (firstIndex !== undefined) {
      return firstIndex.toString();
    }
  }

  return '';
};

export const useConnectionIdxForNavigation = (
  connectionIdx: string
): string => {
  const org = useOrgDomain();
  const lastConnectionIndex = useGetLastConnectionIndex(org);
  const { data } = useSavedConnectionsQuery(
    graphQLClient,
    org ? { org_domain: org } : {},
    {
      enabled: !connectionIdx && lastConnectionIndex !== DEMO_CONNECTION_INDEX,
    }
  );

  if (connectionIdx) {
    return connectionIdx;
  }

  if (
    lastConnectionIndex !== null &&
    (lastConnectionIndex === DEMO_CONNECTION_INDEX ||
      data?.listConnections?.some((c) => c?.index === lastConnectionIndex))
  ) {
    // use the last connection index only when it exists
    // allowed for both orgs and non-orgs
    return lastConnectionIndex.toString();
  }

  if (data?.listConnections?.length) {
    // fall back to the first available connection when nothing else is selected
    // allowed for both orgs and non-orgs
    const firstIndex = data.listConnections[0]?.index;
    if (firstIndex !== undefined) {
      return firstIndex.toString();
    }
  }

  return '';
};
